import React from "react";
import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  MdHome,
  MdInsights,
  MdGroups,
  MdOutlineGridView,
  MdLocationOn,
  MdLogout,
  MdMenu,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

export const MANAGER_SIDEBAR_WIDTH = "264px";

const NAV_ITEMS = [
  { label: "Overview", href: "/manager/engagement#overview", icon: MdHome, section: "overview" },
  { label: "Coaching metrics", href: "/manager/engagement#coaching", icon: MdInsights, section: "coaching" },
  { label: "Employees", href: "/manager/engagement#team", icon: MdGroups, section: "team" },
  { label: "Activity", href: "/manager/engagement#activity", icon: MdOutlineGridView, section: "activity" },
];

function ManagerNavigation({ compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onDashboard = location.pathname === "/manager/engagement" || location.pathname === "/";

  return (
    <VStack align="stretch" spacing={1}>
      {NAV_ITEMS.map((item) => {
        const activeSection = (location.hash || "#overview").replace("#", "");
        const active = onDashboard
          ? activeSection === item.section
          : item.section === "team" && location.pathname.includes("/manager/employees/");
        return (
          <Button
            key={item.label}
            justifyContent={compact ? "center" : "flex-start"}
            leftIcon={compact ? undefined : <Icon as={item.icon} boxSize={5} />}
            variant="ghost"
            color={active ? "#06102F" : "whiteAlpha.700"}
            bg={active ? "white" : "transparent"}
            _hover={{ bg: active ? "white" : "whiteAlpha.100", color: "white" }}
            borderRadius="lg"
            h="44px"
            fontWeight="semibold"
            onClick={() => navigate(item.href)}
            aria-current={active ? "page" : undefined}
            aria-label={item.label}
          >
            {compact ? <Icon as={item.icon} boxSize={5} /> : item.label}
          </Button>
        );
      })}
    </VStack>
  );
}

function Brand({ compact = false }) {
  return (
    <HStack spacing={3} minH="42px" justify={compact ? "center" : "flex-start"}>
      <Image src="/Logo.png" alt="" boxSize="34px" borderRadius="9px" />
      {!compact && <Text color="white" fontSize="xl" fontWeight="semibold">Ethosphere</Text>}
    </HStack>
  );
}

export default function ManagerShell({ children, maxWidth, userName, onSignOut, userInfo }) {
  const navigate = useNavigate();
  const organizationName = userInfo?.organization?.name || "Your dealership";
  const locationName = userInfo?.location?.name || "All authorized locations";
  const fullName = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") || userName;

  return (
    <Box minH="100vh" bg="#F7F7F8">
      <Box
        as="aside"
        position="fixed"
        insetY={0}
        left={0}
        w={MANAGER_SIDEBAR_WIDTH}
        bg="#06102F"
        color="white"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        px={4}
        py={5}
        zIndex={1000}
      >
        <Box px={2} mb={7}><Brand /></Box>
        <ManagerNavigation />
        <Box flex="1" />
        <Box bg="whiteAlpha.100" borderRadius="xl" p={4} mb={4}>
          <HStack align="flex-start" spacing={3}>
            <Flex bg="whiteAlpha.100" color="#4D8DFF" borderRadius="lg" boxSize="36px" align="center" justify="center" flex="0 0 auto">
              <Icon as={MdLocationOn} boxSize={5} />
            </Flex>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{organizationName}</Text>
              <Text mt={1} fontSize="xs" color="whiteAlpha.600" noOfLines={2}>{locationName}</Text>
            </Box>
          </HStack>
        </Box>
        <HStack borderTopWidth="1px" borderColor="whiteAlpha.200" pt={4} px={2} spacing={3}>
          <Avatar size="sm" name={fullName} bg="white" color="#06102F" />
          <Box minW={0} flex="1">
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{fullName}</Text>
            <Text fontSize="xs" color="whiteAlpha.600" noOfLines={1}>{userName}</Text>
          </Box>
          <Button aria-label="Log out" variant="ghost" color="whiteAlpha.700" minW={8} px={1} _hover={{ color: "white", bg: "whiteAlpha.100" }} onClick={onSignOut}>
            <Icon as={MdLogout} boxSize={5} />
          </Button>
        </HStack>
      </Box>

      <Flex
        as="header"
        display={{ base: "flex", lg: "none" }}
        position="sticky"
        top={0}
        zIndex={1000}
        bg="#06102F"
        color="white"
        px={4}
        h="64px"
        align="center"
        justify="space-between"
      >
        <Brand />
        <Menu placement="bottom-end">
          <MenuButton as={Button} variant="ghost" color="white" px={2} _hover={{ bg: "whiteAlpha.100" }} _active={{ bg: "whiteAlpha.200" }} aria-label="Manager navigation">
            <Icon as={MdMenu} boxSize={6} />
          </MenuButton>
          <MenuList color="gray.800" minW="230px">
            {NAV_ITEMS.map((item) => (
              <MenuItem key={item.label} icon={<Icon as={item.icon} />} onClick={() => navigate(item.href)}>{item.label}</MenuItem>
            ))}
            <MenuDivider />
            <MenuItem icon={<MdLogout />} color="red.500" onClick={onSignOut}>Log out</MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <Box ml={{ base: 0, lg: MANAGER_SIDEBAR_WIDTH }}>
        <Container maxW={maxWidth === "container.xl" ? "1480px" : maxWidth} px={{ base: 4, md: 7, xl: 9 }} py={{ base: 5, md: 8 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}
